import { db } from "@/stores/indexed-db";
import { useSetTask } from "tinytick/ui-react";
import { useNetworkState } from "@uidotdev/usehooks";
import { useAuthValue } from "@/stores/auth-store";
import { useUserValue } from "@/stores/user-store";
import { toast } from "sonner";
import { client } from "@/utils/orpc";
import { isErrorResponse } from "@shamba-odk/types";

export const DemoStore = () => {
    const accessToken = useAuthValue("accessToken");
    const user = useUserValue("name");
    const network = useNetworkState();

    useSetTask(
        "syncDemos",
        async () => {
            if (!network.online || !accessToken || !user) {
                return;
            }

            await syncDemos(accessToken, user);
        },
        [accessToken],
        "sync",
        {
            retryDelay: 5000,
            maxRetries: 4
        }
    );

    return null;
}

export const syncDemos = async (token: string, user: string) => {
    try {
        const unsyncedDemos = await db.demos.where({ status: "unsynced" }).toArray();

        if (unsyncedDemos.length === 0) {
            return;
        }

        await Promise.all(
            unsyncedDemos.map(async (demo) => {
                // Remove photo, status, creation, modified
                const doc = {
                    ...demo,
                    user,
                    photo: undefined,
                    status: undefined,
                    creation: undefined,
                    modified: undefined,
                }

                await client.frappe.createDoc({
                    accessToken: token,
                    doctype: "Demo DT",
                    doc: doc,
                }).then((res) => {
                    if (isErrorResponse(res)) {
                        throw new Error(
                            res.error.message ?? res.error.exc_type ?? 'Failed to create demo record'
                        );
                    }

                    db.demos.update(res.data.name, { status: "partial-sync" });

                    if (demo.photo && demo.photo.length > 0) {
                        // Convert FileList to base64 string
                        const file = demo.photo[0];
                        const reader = new FileReader();

                        return new Promise<void>((resolve, reject) => {
                            reader.onload = async (event) => {
                                try {
                                    const base64Data = event.target?.result as string;

                                    // Upload the file
                                    const uploadResponse = await client.frappe.uploadFile({
                                        accessToken: token,
                                        file: base64Data,
                                        docname: res.data.name,
                                        doctype: "Demo DT",
                                        fieldname: "photo",
                                        file_name: `${res.data.name}_photo`,
                                    });

                                    if (isErrorResponse(uploadResponse)) {
                                        throw new Error(uploadResponse.error.exc_type || 'Failed to upload photo');
                                    }

                                    // Extract the file_url from the upload response
                                    const fileUrl = uploadResponse.data.message?.file_url;

                                    if (fileUrl) {
                                        // Update the document with the file_url
                                        const updateResponse = await client.frappe.updateDoc({
                                            accessToken: token,
                                            doctype: "Demo DT",
                                            name: res.data.name,
                                            doc: {
                                                photo: fileUrl
                                            }
                                        });

                                        if (isErrorResponse(updateResponse)) {
                                            throw new Error(updateResponse.error.exc_type || 'Failed to update photo reference');
                                        }
                                        // Document updated with file URL
                                    }

                                    // Mark as synced
                                    await db.demos.update(demo.phone, { status: "synced" });

                                    toast.success(`Synced ${unsyncedDemos.indexOf(demo) + 1}/${unsyncedDemos.length} Demos`)
                                    resolve();
                                } catch (error) {
                                    console.error("[demo] Error Uploading File:", error);
                                    reject(error);
                                }
                            };

                            reader.onerror = () => {
                                reject(new Error("Failed to Read File"));
                            };

                            reader.readAsDataURL(file);
                        });
                    }
                });
            })
        );

        await db.clearSyncedDemos();
    } catch (error) {
        console.error("[demo] Error Syncing Demos:", error);
        toast.error("Error Syncing Demos");
    }
}